import { z } from "zod";
import { apiBigIntIdSchema, apiIntIdSchema } from "./ids.js";
/**
 * Read-only Super Admin view over the existing `AuditLog` table. There is one
 * writer (`writeAuditLog`); this catalog only describes rows it already writes.
 */
export const SUPER_ADMIN_AUDIT_CATEGORIES = [
    "ORGANIZATION",
    "TEAM",
    "AUTH",
    "CONTACTS",
    "CAMPAIGNS",
    "INBOX",
    "CHANNELS",
    "TEMPLATES",
    "SUPER_ADMIN",
];
/** Mirrors the `AuditActorType` enum, minus the reserved API_CLIENT. */
export const SUPER_ADMIN_AUDIT_ACTOR_KINDS = [
    "PLATFORM_ADMIN",
    "ORGANIZATION_MEMBER",
    "SYSTEM",
];
/**
 * Closed list of non-human writers. Must match ck_audit_log_actor_service_key_format.
 * `legacy:unknown` is assigned by the backfill only and is never written by code.
 */
export const AUDIT_SYSTEM_SERVICE_KEYS = [
    "worker:campaign-execution",
    "worker:campaign-audience-import",
    "meta:instagram-deauthorize",
    "meta:instagram-token-revoked",
    "meta:instagram-data-deletion",
    "ops:bootstrap-super-admin",
    "ops:account-lifecycle",
    "ops:channel-key-rotation",
    "system:organization-creation",
];
export const AUDIT_LEGACY_UNKNOWN_SERVICE_KEY = "legacy:unknown";
function beforeAfter(fields) {
    return fields.flatMap((field) => [`${field}Before`, `${field}After`]);
}
/**
 * `detailKeys` is the allowlist of `additionalData` keys a Super Admin may
 * see for that action. Any other key is dropped server-side. Provider IDs,
 * confirmation codes, idempotency keys, and free-text failure summaries are
 * deliberately absent.
 */
export const SUPER_ADMIN_AUDIT_ACTIONS = {
    ORGANIZATION_CREATED: { category: "ORGANIZATION", detailKeys: [] },
    ORGANIZATION_UPDATED: {
        category: "ORGANIZATION",
        detailKeys: ["previousName", "name", "previousTimezone", "timezone"],
    },
    MEMBER_ROLE_CHANGED: { category: "TEAM", detailKeys: ["previousRole", "role"] },
    MEMBER_REMOVED: { category: "TEAM", detailKeys: ["role"] },
    MEMBER_DISPLAY_NAME_CHANGED: { category: "TEAM", detailKeys: [] },
    INVITATION_CREATED: { category: "TEAM", detailKeys: ["invitedRole"] },
    INVITATION_RESENT: { category: "TEAM", detailKeys: ["invitedRole"] },
    INVITATION_CANCELLED: { category: "TEAM", detailKeys: ["invitedRole"] },
    INVITATION_ACCEPTED: {
        category: "TEAM",
        detailKeys: ["invitedRole", "restoredExistingMembership"],
    },
    AUTH_ACCOUNT_SOFT_DELETED: { category: "AUTH", detailKeys: ["revokedSessionCount"] },
    USER_SOFT_DELETED: { category: "AUTH", detailKeys: ["revokedSessionCount"] },
    CONTACT_CREATED: { category: "CONTACTS", detailKeys: [] },
    CONTACT_UPDATED: { category: "CONTACTS", detailKeys: [] },
    CONTACT_SOFT_DELETED: { category: "CONTACTS", detailKeys: [] },
    CONTACT_RESTORED: { category: "CONTACTS", detailKeys: [] },
    CONTACT_MERGED: {
        category: "CONTACTS",
        detailKeys: ["secondaryContactId", "reassignedProfileIds"],
    },
    CONTACT_LINKED_TO_PROFILE: {
        category: "CONTACTS",
        detailKeys: ["profileId", "platformType", "source"],
    },
    CONTACT_CREATED_FROM_PROFILE: {
        category: "CONTACTS",
        detailKeys: ["profileId", "previousContactId"],
    },
    CONTACT_GROUPS_BULK_UPDATED: { category: "CONTACTS", detailKeys: ["mode", "contactCount"] },
    CONTACTS_BULK_SOFT_DELETED: { category: "CONTACTS", detailKeys: ["contactCount"] },
    CONTACTS_IMPORTED: {
        category: "CONTACTS",
        detailKeys: ["importStatus", "createdCount", "restoredCount", "skippedCount", "failedCount"],
    },
    CONTACT_GROUP_CREATED: { category: "CONTACTS", detailKeys: ["name"] },
    CONTACT_GROUP_RENAMED: { category: "CONTACTS", detailKeys: ["previousName", "name"] },
    CONTACT_GROUP_DELETED: { category: "CONTACTS", detailKeys: ["name"] },
    CONTACT_IMPORT_JOB_UPLOADED: {
        category: "CONTACTS",
        detailKeys: ["originalFileName", "byteSize", "sourceFormat"],
    },
    CAMPAIGN_CREATED: { category: "CAMPAIGNS", detailKeys: ["name"] },
    CAMPAIGN_DELETED: { category: "CAMPAIGNS", detailKeys: ["name"] },
    CAMPAIGN_AUDIENCE_DEFINITION_UPDATED: {
        category: "CAMPAIGNS",
        detailKeys: ["audienceDefinitionRevision", "contactCount", "groupCount"],
    },
    CAMPAIGN_AUDIENCE_SNAPSHOT_COMMITTED: {
        category: "CAMPAIGNS",
        detailKeys: [
            "audienceDefinitionRevision",
            "selectedSourceEntries",
            "canonicalRecipients",
            "readyRecipients",
            "excludedBeforeRecipient",
            "duplicateSourceEntries",
            "csvConsumed",
        ],
    },
    CAMPAIGN_MESSAGE_CONFIG_UPDATED: {
        category: "CAMPAIGNS",
        detailKeys: [
            "platformEndpointId",
            "whatsappTemplateId",
            "templateName",
            "templateLanguage",
            "parameterFormat",
            "mappingSources",
            "messageConfigured",
        ],
    },
    CAMPAIGN_STARTED: {
        category: "CAMPAIGNS",
        detailKeys: [
            "executableRecipientCount",
            "excludedRecipientCount",
            "creditsReserved",
        ],
    },
    CAMPAIGN_SCHEDULED: {
        category: "CAMPAIGNS",
        detailKeys: [
            "scheduledAt",
            "executableRecipientCount",
            "excludedRecipientCount",
            "creditsReserved",
        ],
    },
    CAMPAIGN_SCHEDULE_CANCELED: { category: "CAMPAIGNS", detailKeys: [] },
    CAMPAIGN_SEND_TEST: {
        category: "CAMPAIGNS",
        detailKeys: ["destination", "sampleRecipientId", "messageId", "campaignStatus"],
    },
    CAMPAIGN_FAILED: { category: "CAMPAIGNS", detailKeys: ["failureCode"] },
    CAMPAIGN_COMPLETED: { category: "CAMPAIGNS", detailKeys: [] },
    CAMPAIGN_EXECUTION_INTEGRITY_HOLD: {
        category: "CAMPAIGNS",
        detailKeys: ["code", "reason", "creditsRemaining", "creditsReserved", "recipientId"],
    },
    CAMPAIGN_AUDIENCE_CSV_UPLOADED: {
        category: "CAMPAIGNS",
        detailKeys: [
            "uploadId",
            "originalFileName",
            "byteSize",
            "replacedUploadId",
            "audienceDefinitionRevision",
        ],
    },
    CAMPAIGN_AUDIENCE_CSV_CONFIGURED: {
        category: "CAMPAIGNS",
        detailKeys: [
            "uploadId",
            "phoneColumnKey",
            "defaultCountryIso",
            "hasHeader",
            "audienceDefinitionRevision",
        ],
    },
    CAMPAIGN_AUDIENCE_CSV_REMOVED: {
        category: "CAMPAIGNS",
        detailKeys: ["uploadId", "audienceDefinitionRevision"],
    },
    CAMPAIGN_AUDIENCE_CSV_IMPORT_FAILED: {
        category: "CAMPAIGNS",
        detailKeys: ["uploadId", "campaignId", "errorCode"],
    },
    /** Written only for Platform Admin sends as the acting OWNER; entityId is the Message id. */
    INBOX_MESSAGE_SENT: {
        category: "INBOX",
        detailKeys: ["conversationId", "platformType", "attachmentIds"],
    },
    INBOX_TEMPLATE_SENT: {
        category: "INBOX",
        detailKeys: ["conversationId", "platformType", "whatsappTemplateId"],
    },
    /** Platform Admin assignment changes. Members are recorded on ConversationEvent only. */
    CONVERSATION_ASSIGNED: {
        category: "INBOX",
        detailKeys: ["fromAssignedMemberId", "toAssignedMemberId"],
    },
    CONVERSATION_REASSIGNED: {
        category: "INBOX",
        detailKeys: ["fromAssignedMemberId", "toAssignedMemberId"],
    },
    CONVERSATION_UNASSIGNED: {
        category: "INBOX",
        detailKeys: ["fromAssignedMemberId", "toAssignedMemberId"],
    },
    CONVERSATION_CLOSED: {
        category: "INBOX",
        detailKeys: ["conversationStatusBefore", "conversationStatusAfter"],
    },
    CONVERSATION_REOPENED: {
        category: "INBOX",
        detailKeys: ["conversationStatusBefore", "conversationStatusAfter"],
    },
    /** Every export, including organization members. Platform Admin keeps member id null. */
    CONVERSATION_EXPORTED: {
        category: "INBOX",
        detailKeys: ["conversationId", "format"],
    },
    PLATFORM_ACCOUNT_CONNECTED: {
        category: "CHANNELS",
        detailKeys: ["platformType", "phase", "endpointCount", "selectedPlatformEndpointId"],
    },
    PLATFORM_ACCOUNT_DISCONNECTED: {
        category: "CHANNELS",
        detailKeys: ["platformType", "phase", "trigger"],
    },
    CHANNEL_CREDENTIAL_KEY_ROTATED: {
        category: "CHANNELS",
        detailKeys: [
            "strategy",
            "mode",
            "sourceKeyVersion",
            "targetKeyVersion",
            "whatsappTokensReencrypted",
            "facebookTokensReencrypted",
            "instagramTokensReencrypted",
            "pinsMigrated",
            "skippedUndecryptableNonWhatsApp",
        ],
    },
    WHATSAPP_PHONE_SWITCHED: {
        category: "CHANNELS",
        detailKeys: [
            "platformType",
            "previousPlatformEndpointId",
            "selectedPlatformEndpointId",
            "attachedExistingWabaPhone",
        ],
    },
    WHATSAPP_PHONE_REGISTERED: {
        category: "CHANNELS",
        detailKeys: [
            "platformType",
            "previousPlatformEndpointId",
            "selectedPlatformEndpointId",
            "attachedExistingWabaPhone",
            "registeredViaApi",
        ],
    },
    INSTAGRAM_DATA_DELETION_COMPLETED: {
        category: "CHANNELS",
        detailKeys: ["requestedAt", "completedAt", "status", "trigger", "connectionFound"],
    },
    TEMPLATE_CREATED: {
        category: "TEMPLATES",
        detailKeys: ["name", "language", "category", "status"],
    },
    TEMPLATE_EDITED: {
        category: "TEMPLATES",
        detailKeys: ["name", "language", "category", "status"],
    },
    TEMPLATE_DELETED: {
        category: "TEMPLATES",
        detailKeys: ["name", "language", "category", "status"],
    },
    ORGANIZATION_STATUS_UPDATED: {
        category: "SUPER_ADMIN",
        detailKeys: ["statusBefore", "statusAfter"],
    },
    ORGANIZATION_BETA_UPDATED: {
        category: "SUPER_ADMIN",
        detailKeys: beforeAfter(["isBetaOrganization"]),
    },
    BETA_FEATURE_UPDATED: {
        category: "SUPER_ADMIN",
        detailKeys: ["featureKey", ...beforeAfter(["betaEnabled"])],
    },
    ORGANIZATION_ENTITLEMENT_UPDATED: {
        category: "SUPER_ADMIN",
        detailKeys: beforeAfter([
            "contactsLimit",
            "teamMembersLimit",
            "storageQuotaBytes",
            "whatsappEnabled",
            "facebookEnabled",
            "instagramEnabled",
        ]),
    },
    ORGANIZATION_MESSAGE_BALANCE_UPDATED: {
        category: "SUPER_ADMIN",
        detailKeys: [
            "operation",
            "amount",
            "forfeitedCredits",
            ...beforeAfter([
                "isUnlimited",
                "availableCredits",
                "reservedCredits",
                "usedCredits",
                "totalAddedCredits",
                "totalDeductedCredits",
            ]),
        ],
    },
    MESSAGE_CREDIT_BLOCKED_JOBS_RESUMED: {
        category: "ORGANIZATION",
        detailKeys: ["resumedCount", "creditsReserved"],
    },
    MESSAGE_CREDIT_BLOCKED_JOBS_DISCARDED: {
        category: "ORGANIZATION",
        detailKeys: ["discardedCount"],
    },
    SYSTEM_CONFIG_UPDATED: {
        category: "SUPER_ADMIN",
        detailKeys: beforeAfter([
            "defaultContactsLimit",
            "defaultTeamMembersLimit",
            "defaultStorageQuotaBytes",
            "defaultWhatsAppEnabled",
            "defaultFacebookEnabled",
            "defaultInstagramEnabled",
            "defaultMessageBalanceUnlimited",
            "defaultMessageCredits",
        ]),
    },
    SYSTEM_SETTINGS_UPDATED: {
        category: "SUPER_ADMIN",
        detailKeys: beforeAfter(["supportEmail", "supportPhone", "supportWhatsApp", "supportUrl"]),
    },
    SESSION_REVOKED: {
        category: "SUPER_ADMIN",
        detailKeys: ["targetUserId", "revokedSessionCount"],
    },
    SESSIONS_REVOKED: {
        category: "SUPER_ADMIN",
        detailKeys: ["targetUserId", "revokedSessionCount"],
    },
    PLATFORM_ADMIN_GRANTED: {
        category: "SUPER_ADMIN",
        detailKeys: ["targetUserId", "restored"],
    },
    PLATFORM_ADMIN_REMOVED: {
        category: "SUPER_ADMIN",
        detailKeys: ["targetUserId", "selfRemoval"],
    },
};
export const SUPER_ADMIN_AUDIT_ACTION_TYPES = Object.keys(SUPER_ADMIN_AUDIT_ACTIONS);
export function isSuperAdminAuditActionType(value) {
    return Object.prototype.hasOwnProperty.call(SUPER_ADMIN_AUDIT_ACTIONS, value);
}
const AUDIT_ACTION_ORGANIZATION_SCOPE_OVERRIDES = {
    AUTH_ACCOUNT_SOFT_DELETED: "NONE",
    USER_SOFT_DELETED: "NONE",
    SYSTEM_CONFIG_UPDATED: "NONE",
    SYSTEM_SETTINGS_UPDATED: "NONE",
    SESSION_REVOKED: "NONE",
    SESSIONS_REVOKED: "NONE",
    PLATFORM_ADMIN_GRANTED: "NONE",
    PLATFORM_ADMIN_REMOVED: "NONE",
    BETA_FEATURE_UPDATED: "NONE",
    INSTAGRAM_DATA_DELETION_COMPLETED: "OPTIONAL",
    CHANNEL_CREDENTIAL_KEY_ROTATED: "NONE",
};
export function auditActionOrganizationScope(action) {
    return AUDIT_ACTION_ORGANIZATION_SCOPE_OVERRIDES[action] ?? "REQUIRED";
}
/** Super Admin category actions can never be written by an organization member. */
export function auditActionAllowsOrganizationMember(action) {
    if (action === "MESSAGE_CREDIT_BLOCKED_JOBS_RESUMED" || action === "MESSAGE_CREDIT_BLOCKED_JOBS_DISCARDED") {
        return true; // Used by OWNER/ADMIN
    }
    return SUPER_ADMIN_AUDIT_ACTIONS[action].category !== "SUPER_ADMIN";
}
export function superAdminAuditActionsInCategory(category) {
    return SUPER_ADMIN_AUDIT_ACTION_TYPES.filter((action) => SUPER_ADMIN_AUDIT_ACTIONS[action].category === category);
}
/** Every detail key that can ever reach the client. */
export const SUPER_ADMIN_AUDIT_DETAIL_KEYS = [
    ...new Set(SUPER_ADMIN_AUDIT_ACTION_TYPES.flatMap((action) => SUPER_ADMIN_AUDIT_ACTIONS[action].detailKeys)),
];
/** Entity types the current writers use. Unknown types render a generic label. */
export const SUPER_ADMIN_AUDIT_ENTITY_TYPES = [
    "Organization",
    "OrganizationEntitlement",
    "OrganizationMember",
    "Invitation",
    "User",
    "AuthAccount",
    "Session",
    "PlatformAdmin",
    "SystemConfig",
    "SystemSettings",
    "Contact",
    "ContactGroup",
    "ContactImportJob",
    "Campaign",
    "CampaignAudienceImportJob",
    "Message",
    "Conversation",
    "PlatformAccount",
    "WhatsAppTemplate",
    "OrganizationMessageBalance",
    "BetaFeature",
];
export const superAdminAuditLogListQuerySchema = z
    .object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().positive().max(50).optional(),
    category: z.enum(SUPER_ADMIN_AUDIT_CATEGORIES).optional(),
    actionType: z.string().min(1).max(100).refine(isSuperAdminAuditActionType).optional(),
    actorKind: z.enum(SUPER_ADMIN_AUDIT_ACTOR_KINDS).optional(),
    userId: z.coerce.number().int().positive().optional(),
    organizationId: z.coerce.number().int().positive().optional(),
    /** Inclusive lower bound on `occurredAt`. */
    from: z.string().datetime({ offset: true }).optional(),
    /** Exclusive upper bound on `occurredAt`. */
    to: z.string().datetime({ offset: true }).optional(),
})
    .strict();
export const superAdminAuditDetailSchema = z.object({
    key: z.string(),
    value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
});
export const superAdminAuditLogItemSchema = z.object({
    id: apiBigIntIdSchema,
    occurredAt: z.string(),
    actionType: z.string(),
    actor: z.object({
        kind: z.enum(SUPER_ADMIN_AUDIT_ACTOR_KINDS),
        userId: apiIntIdSchema.nullable(),
        displayName: z.string().nullable(),
        email: z.string().nullable(),
        /** Set only for SYSTEM actors, e.g. `worker:campaign-execution` or `legacy:unknown`. */
        serviceKey: z.string().nullable(),
    }),
    /**
     * Platform Admin rows only: the organization's acting OWNER stamped on the
     * business record. Internal to Super Admin; never shown to customers.
     */
    actingAs: z
        .object({
        organizationMemberId: apiIntIdSchema,
        displayName: z.string().nullable(),
    })
        .nullable(),
    organization: z
        .object({
        id: apiIntIdSchema,
        name: z.string(),
    })
        .nullable(),
    entity: z.object({
        type: z.string(),
        id: z.string().nullable(),
    }),
    /** Allowlisted and masked; never the raw `additionalData`. */
    details: z.array(superAdminAuditDetailSchema),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
});
export const superAdminAuditLogListResponseSchema = z.object({
    items: z.array(superAdminAuditLogItemSchema),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=super-admin-audit-contracts.js.map